'use client';

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RoomEvent, type Room, type RemoteParticipant } from 'livekit-client';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFileGrantMode } from '@/store/slices/toolSharingSlice';
import { isTrustedOfficerIdentity } from '@/lib/participantUtils';
import { LiveKitDataChunkReassembler } from '@/lib/livekit/dataTransport';
import { publishDataGuarded } from '@/lib/livekit/publishDataGuarded';
import { FILE_GRANT_TOPIC, fileGrantPermissions, fileGrantPermissionsKey, isFileGrantScope, isGrantUuid, matchesGrantTarget,
  parseFileGrantPacket, sameGrantScope, type FileGrantContext as Bootstrap, type FileGrantReceipt,
  type FileGrantScope, type FileGrantTarget } from '@/lib/files/fileGrantProtocol';

type Runtime = { selected: boolean; accessToken: string | null; scope: FileGrantScope | null | undefined;
  permissionsReady: boolean;
  fileAuthority: 'unknown' | 'lead' | 'forbidden';
  recoveryAvailable: boolean; recovering: boolean; recoveryError: boolean; recover(): void;
  scopeUpdatedAt: number; request(): void; reject(accessToken?: string): void; observeScope(value: unknown, timestamp: number): void };
const inert: Runtime = { selected: false, accessToken: null, scope: undefined, scopeUpdatedAt: 0, permissionsReady: true, fileAuthority: 'unknown',
  recoveryAvailable: false, recovering: false, recoveryError: false, recover() {}, request() {}, reject() {}, observeScope() {} };
class FileDeliveryError extends Error {
  constructor(readonly status: number, readonly code: unknown) { super('File delivery unavailable'); }
}
const Context = createContext<Runtime>(inert);
export const usePrivateFileGrant = () => useContext(Context);

/** Exactly one owner per joined Room. Tokens and pending nonces stay in memory. */
export function FileGrantProvider({ room, bootstrap, isOfficer, children }: {
  room: Room; bootstrap?: Bootstrap; isOfficer: boolean; children: ReactNode;
}) {
  // The Room owner keys this component by its join resource. Peer state and later
  // connection-details changes cannot select or downgrade a joined call's mode.
  const [joined] = useState(() => bootstrap);
  const selected = joined?.version === 2;
  const dispatch = useAppDispatch();
  const sharing = useAppSelector(state => state.toolSharing);
  const permissions = useMemo(() => fileGrantPermissions({ ...sharing,
    sharedItems: sharing.sharedTools.includes('files') ? sharing.sharedItems : [] }),
  [sharing.sharedItems, sharing.callerUploadFolder, sharing.sharedTools]);
  const permissionKey = fileGrantPermissionsKey(permissions);
  const current = useRef({ permissions, permissionKey, isOfficer });
  current.current = { permissions, permissionKey, isOfficer };
  const [credential, setCredential] = useState<FileGrantReceipt | null>(null);
  const credentialRef = useRef<FileGrantReceipt | null>(null);
  const pending = useRef<FileGrantTarget | null>(null);
  const serial = useRef<Promise<unknown>>(Promise.resolve());
  const incarnation = useRef(0);
  const observedScope = useRef<FileGrantScope | null | undefined>(undefined);
  const observedAt = useRef(0);
  const [announcement, setAnnouncement] = useState<{ scope: FileGrantScope | null | undefined; at: number }>({ scope: undefined, at: 0 });
  const [confirmedPermissionKey, setConfirmedPermissionKey] = useState<string | null>(null);
  const [fileAuthority, setFileAuthority] = useState<Runtime['fileAuthority']>('unknown');
  const [recoveryAvailable, setRecoveryAvailable] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState(false);
  const recoverAction = useRef<() => void>(() => {});
  const recover = useCallback(() => recoverAction.current(), []);
  const validNow = useCallback(() => selected && isGrantUuid(joined?.callRowId) && joined?.roomName === room.name,
    [joined, room, selected]);

  useLayoutEffect(() => {
    dispatch(selectFileGrantMode({ roomName: joined?.roomName ?? room.name, version: selected ? 2 : null }));
  }, [dispatch, joined, room, selected]);

  const clear = useCallback(() => {
    pending.current = null; credentialRef.current = null; setCredential(null);
  }, []);
  const request = useCallback(() => {
    if (!validNow() || current.current.isOfficer || room.state !== 'connected' || !room.localParticipant.sid) return;
    if (observedScope.current === null) return;
    if (credentialRef.current && BigInt(credentialRef.current.expiresAtMs) > BigInt(Date.now())) return;
    const previous = pending.current;
    const target: FileGrantTarget = previous && previous.participantSid === room.localParticipant.sid &&
      previous.participantIdentity === room.localParticipant.identity ? previous : { callRowId: joined!.callRowId!, roomName: joined!.roomName,
      participantIdentity: room.localParticipant.identity, participantSid: room.localParticipant.sid,
      requestNonce: crypto.randomUUID() };
    pending.current = target;
    void publishDataGuarded(room, new TextEncoder().encode(JSON.stringify({ type: 'request', ...target })),
      { reliable: true, topic: FILE_GRANT_TOPIC }).catch(() => { /* Retry the same seat/nonce after ambiguous delivery. */ });
  }, [joined, room, validNow]);
  const reject = useCallback((accessToken?: string) => {
    if (accessToken !== undefined && credentialRef.current?.accessToken !== accessToken) return;
    clear(); request();
  }, [clear, request]);
  const observeScope = useCallback((value: unknown, timestamp: number) => {
    if (!selected || value === undefined || (!isFileGrantScope(value) && value !== null) ||
        !Number.isFinite(timestamp) || timestamp < observedAt.current) return;
    if (value === null || !sameGrantScope(observedScope.current, value)) {
      observedScope.current = value; observedAt.current = timestamp;
      clear(); if (value !== null) request();
    } else observedAt.current = timestamp;
  }, [clear, request, selected]);

  const announce = useCallback((scope: FileGrantScope | null) => {
    observedScope.current = scope;
    setAnnouncement(old => sameGrantScope(old.scope, scope) || (old.scope === null && scope === null) ? old :
      { scope, at: Math.max(Date.now(), old.at + 1) });
  }, []);

  useEffect(() => {
    if (!selected) return;
    let alive = true;
    const generation = ++incarnation.current;
    let sessionGeneration = 0;
    const controllers = new Set<AbortController>();
    type RecoveryCandidate = { scope: FileGrantScope; orgUnitId: string | null; session: number;
      participantIdentity: string; participantSid: string };
    let recoveryCandidate: RecoveryCandidate | null = null, recoveryRunning = false;
    const sameRecoverySeat = (candidate: RecoveryCandidate) => {
      const seat = room.remoteParticipants.get(candidate.participantIdentity);
      return seat?.identity === candidate.participantIdentity && seat.sid === candidate.participantSid;
    };
    const resetRecovery = () => { recoveryCandidate = null; setRecoveryAvailable(false); setRecoveryError(false); };
    resetRecovery(); setRecovering(false);
    const fresh = (session = sessionGeneration) => alive && incarnation.current === generation &&
      session === sessionGeneration && validNow() && room.state === 'connected';
    const post = async (endpoint: string, body: unknown) => {
      const session = sessionGeneration;
      const controller = new AbortController(); controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 60000);
      try {
        const response = await fetch('/api/files/grants/' + endpoint, { method: 'POST', cache: 'no-store',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal });
        const value = await response.json();
        if (!fresh(session)) throw new Error('Joined file scope changed');
        if (!response.ok) {
          if (endpoint === 'state' && (response.status === 403 ||
              (response.status === 409 && value?.status === 'scope_conflict'))) { setFileAuthority('forbidden'); resetRecovery(); }
          if (endpoint !== 'state' && (response.status === 403 ||
              (response.status === 409 && value?.status === 'scope_conflict'))) {
            setFileAuthority('unknown'); setConfirmedPermissionKey(null); resetRecovery();
          }
          throw new FileDeliveryError(response.status, value?.status);
        }
        return value;
      } finally { clearTimeout(timer); controllers.delete(controller); }
    };
    const ensureHead = async () => {
      if (!fresh() || !current.current.isOfficer) return null;
      const captured = current.current;
      const state = await post('state', { callRowId: joined!.callRowId });
      if (state.callRowId !== joined!.callRowId || state.roomName !== joined!.roomName ||
          (state.orgUnitId !== null && !isGrantUuid(state.orgUnitId))) return null;
      setFileAuthority('lead');
      let head = state.head;
      if (state.status === 'closed' || head?.phase === 'closed') { setConfirmedPermissionKey(null); announce(null); return null; }
      if (head?.phase === 'pending') {
        // A reload loses local attempt memory. Resume the service-owned pending
        // operation; only durable custody knows its original predecessor.
        head = (await post('resume', { callRowId: joined!.callRowId, orgUnitId: state.orgUnitId })).head;
        if (!head || head.callRowId !== joined!.callRowId || (head.phase !== 'active' && head.phase !== 'closed')) return null;
        if (head.phase === 'closed') { setConfirmedPermissionKey(null); announce(null); return null; }
      }
      if (!head || head.phase !== 'active' || fileGrantPermissionsKey(head.permissions) !== captured.permissionKey) {
        if (head && !isFileGrantScope({ epoch: head.epoch, revision: head.revision, operationId: head.operationId })) return null;
        const expected = head ? { epoch: head.epoch, revision: head.revision, operationId: head.operationId, phase: head.phase } : null;
        const operation = { epoch: head?.epoch ?? crypto.randomUUID(), revision: head ? String(BigInt(head.revision) + 1n) : '1',
          operationId: crypto.randomUUID(), kind: !head ? 'initialize' : 'replace',
          permissions: captured.permissions };
        const result = await post('operation', { callRowId: joined!.callRowId, orgUnitId: state.orgUnitId, operation, expected });
        head = result.head;
      }
      if (!head || head.phase !== 'active' || head.callRowId !== joined!.callRowId ||
          fileGrantPermissionsKey(head.permissions) !== captured.permissionKey || current.current.permissionKey !== captured.permissionKey) return null;
      const scope = { epoch: head.epoch, revision: head.revision, operationId: head.operationId };
      if (!isFileGrantScope(scope)) return null;
      announce(scope);
      setConfirmedPermissionKey(captured.permissionKey);
      return { scope, orgUnitId: state.orgUnitId, permissionKey: captured.permissionKey };
    };
    const enqueue = (task: () => Promise<unknown>) => {
      serial.current = serial.current.catch(() => {}).then(task).catch(() => { /* Remain unavailable; no legacy downgrade. */ });
    };
    recoverAction.current = () => {
      const candidate = recoveryCandidate;
      if (!candidate || recoveryRunning || !current.current.isOfficer || !fresh(candidate.session) ||
          !sameGrantScope(observedScope.current, candidate.scope)) return;
      if (!sameRecoverySeat(candidate)) { resetRecovery(); return; }
      recoveryRunning = true; setRecovering(true); setRecoveryError(false); setConfirmedPermissionKey(null);
      enqueue(async () => {
        try {
          if (!fresh(candidate.session) || recoveryCandidate !== candidate) return;
          if (!sameRecoverySeat(candidate)) { resetRecovery(); return; }
          const state = await post('state', { callRowId: joined!.callRowId });
          if (state.callRowId !== joined!.callRowId || state.roomName !== joined!.roomName || state.orgUnitId !== candidate.orgUnitId)
            throw new Error('Canonical file scope changed');
          // The unavailable issuance belongs to this exact caller connection,
          // not a departed seat or a replacement SID after the state await.
          if (!sameRecoverySeat(candidate)) { resetRecovery(); return; }
          let head = state.head;
          if (state.status === 'closed') { resetRecovery(); announce(null); return; }
          if (!head || head.callRowId !== joined!.callRowId) { resetRecovery(); throw new Error('File authority missing'); }
          if (head.phase === 'closed') { resetRecovery(); announce(null); return; }
          if (head.phase === 'pending') {
            head = (await post('resume', { callRowId: joined!.callRowId, orgUnitId: state.orgUnitId })).head;
          } else if (head.phase === 'active' && sameGrantScope(head, candidate.scope)) {
            head = (await post('recover', { callRowId: joined!.callRowId, orgUnitId: state.orgUnitId })).head;
          } else { resetRecovery(); return; }
          if (!head || head.callRowId !== joined!.callRowId) throw new Error('Recovered file authority unavailable');
          if (head.phase === 'closed') { resetRecovery(); announce(null); return; }
          const scope = { epoch: head.epoch, revision: head.revision, operationId: head.operationId };
          if (head.phase !== 'active' || !isFileGrantScope(scope) || sameGrantScope(scope, candidate.scope))
            throw new Error('Recovered file authority unavailable');
          if (!fresh(candidate.session)) return;
          clear(); announce(scope);
          if (fileGrantPermissionsKey(head.permissions) === current.current.permissionKey)
            setConfirmedPermissionKey(current.current.permissionKey);
          resetRecovery();
        } catch (_) {
          if (fresh(candidate.session)) { setConfirmedPermissionKey(null); setRecoveryError(true); }
        } finally {
          recoveryRunning = false;
          if (fresh(candidate.session)) setRecovering(false);
        }
      });
    };
    const reassembler = new LiveKitDataChunkReassembler();
    const queuedSeats = new Set<string>();
    const receive = (bytes: Uint8Array, sender?: RemoteParticipant, _kind?: unknown, topic?: string) => {
      if (topic !== FILE_GRANT_TOPIC || !sender || !fresh()) return;
      const payload = reassembler.accept(bytes, { scope: room.name, topic, senderIdentity: sender.identity });
      if (!payload) return;
      let parsed: ReturnType<typeof parseFileGrantPacket>;
      try { parsed = parseFileGrantPacket(JSON.parse(new TextDecoder().decode(payload))); } catch { return; }
      if (!parsed) return;
      if (parsed.type === 'request') {
        if (!current.current.isOfficer || parsed.callRowId !== joined!.callRowId || parsed.roomName !== room.name ||
            parsed.participantIdentity !== sender.identity || parsed.participantSid !== sender.sid) return;
        const target = { callRowId: parsed.callRowId, roomName: parsed.roomName, participantIdentity: sender.identity,
          participantSid: sender.sid, requestNonce: parsed.requestNonce };
        const requestKey = JSON.stringify(target);
        const requestedSession = sessionGeneration;
        if (queuedSeats.has(requestKey)) return;
        queuedSeats.add(requestKey);
        enqueue(async () => {
          let active: Awaited<ReturnType<typeof ensureHead>> = null;
          try {
          if (!fresh(requestedSession)) return;
          const head = await ensureHead(); active = head;
          if (!head || !fresh() || room.remoteParticipants.get(sender.identity)?.sid !== target.participantSid) return;
          const reply = await post('caller', { callRowId: target.callRowId, orgUnitId: head.orgUnitId,
            participantIdentity: target.participantIdentity, participantSid: target.participantSid, requestNonce: target.requestNonce });
          const receipt = parseFileGrantPacket({ type: 'receipt', target: reply.target, scope: reply.scope,
            generation: reply.generation, expiresAtMs: reply.expiresAtMs, accessToken: reply.accessToken });
          if (!receipt || receipt.type !== 'receipt' || !matchesGrantTarget(receipt.target, target) ||
              !sameGrantScope(receipt.scope, head.scope) || current.current.permissionKey !== head.permissionKey ||
              BigInt(receipt.expiresAtMs) <= BigInt(Date.now()) || !fresh(requestedSession) ||
              room.remoteParticipants.get(sender.identity)?.sid !== target.participantSid) return;
          resetRecovery();
          await publishDataGuarded(room, new TextEncoder().encode(JSON.stringify(receipt)),
            { reliable: true, topic: FILE_GRANT_TOPIC, destinationIdentities: [target.participantIdentity] });
          } catch (error) {
            // A connected seat's ordinary stale/expired refusal is not evidence
            // of lost global delivery custody. Offer restoration only after an
            // unavailable issuance under this exact current ACTIVE lead scope.
            if (error instanceof FileDeliveryError && error.status === 503 &&
                (error.code === 'unavailable' || error.code === 'scope_unavailable') && active &&
                fresh(requestedSession) && sameGrantScope(observedScope.current, active.scope) &&
                room.remoteParticipants.get(sender.identity)?.sid === target.participantSid) {
              setConfirmedPermissionKey(null);
              // Failure is not authority: check canonical ownership and the
              // durable ACTIVE version again before offering the lead action.
              try {
                const state = await post('state', { callRowId: joined!.callRowId });
                if (fresh(requestedSession) && state.callRowId === joined!.callRowId && state.roomName === joined!.roomName &&
                    state.orgUnitId === active.orgUnitId && state.head?.callRowId === joined!.callRowId &&
                    state.head.phase === 'active' && sameGrantScope(state.head, active.scope) &&
                    sameGrantScope(observedScope.current, active.scope) &&
                    room.remoteParticipants.get(sender.identity)?.sid === target.participantSid) {
                  recoveryCandidate = { scope: active.scope, orgUnitId: active.orgUnitId, session: requestedSession,
                    participantIdentity: target.participantIdentity, participantSid: target.participantSid };
                  setRecoveryAvailable(true); setRecoveryError(false);
                } else resetRecovery();
              } catch (_) { resetRecovery(); }
            }
          } finally { queuedSeats.delete(requestKey); }
        });
      } else {
        if (current.current.isOfficer || !isTrustedOfficerIdentity(sender.identity) || !pending.current ||
            !matchesGrantTarget(parsed.target, pending.current) || parsed.target.participantSid !== room.localParticipant.sid ||
            parsed.target.participantIdentity !== room.localParticipant.identity || BigInt(parsed.expiresAtMs) <= BigInt(Date.now()) ||
            (observedScope.current !== undefined && !sameGrantScope(parsed.scope, observedScope.current))) return;
        const previous = credentialRef.current;
        if (previous && sameGrantScope(previous.scope, parsed.scope) && BigInt(parsed.generation) < BigInt(previous.generation)) return;
        pending.current = null; observedScope.current = parsed.scope; credentialRef.current = parsed; setCredential(parsed);
      }
    };
    const connected = () => { ++sessionGeneration; clear(); if (current.current.isOfficer) enqueue(ensureHead); else request(); };
    const disconnect = () => { ++sessionGeneration; clear(); resetRecovery(); setRecovering(false); for (const controller of controllers) controller.abort(); reassembler.clear(); };
    room.on(RoomEvent.DataReceived, receive); room.on(RoomEvent.Connected, connected);
    room.on(RoomEvent.Reconnected, connected); room.on(RoomEvent.Reconnecting, disconnect); room.on(RoomEvent.Disconnected, disconnect);
    connected();
    const retry = setInterval(() => {
      if (fresh() && !current.current.isOfficer && !credentialRef.current) request();
    }, 5000);
    return () => {
      alive = false; ++incarnation.current; recoverAction.current = () => {}; disconnect(); clearInterval(retry);
      room.off(RoomEvent.DataReceived, receive); room.off(RoomEvent.Connected, connected);
      room.off(RoomEvent.Reconnected, connected); room.off(RoomEvent.Reconnecting, disconnect); room.off(RoomEvent.Disconnected, disconnect);
    };
  }, [selected, joined, room, announce, clear, request, permissionKey, validNow]);

  useEffect(() => {
    if (!credential) return;
    const delay = Number(BigInt(credential.expiresAtMs) - BigInt(Date.now()));
    // setTimeout's signed 32-bit delay is an actual browser protocol bound.
    const timer = setTimeout(reject, Math.max(0, Math.min(delay, 2147483647)));
    return () => clearTimeout(timer);
  }, [credential, reject]);
  const value = useMemo<Runtime>(() => ({ selected, accessToken: credential?.accessToken ?? null,
    permissionsReady: !selected || confirmedPermissionKey === permissionKey, fileAuthority,
    recoveryAvailable, recovering, recoveryError, recover,
    scope: announcement.scope, scopeUpdatedAt: announcement.at, request, reject, observeScope }),
  [selected, credential, announcement, request, reject, observeScope, confirmedPermissionKey, permissionKey, fileAuthority, recoveryAvailable, recovering, recoveryError, recover]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
